import { Stores } from '../../../libraries/storage/constants';
import { deleteData, deleteDataByRange, getDataByKey, getDataByRange } from '../../../libraries/storage/idb';
import type { AnnotationFrameRecord, AnnotationMetadataRecord, FetchedRangesRecord } from '../../../libraries/storage/types';
import { filterStaticAnswers as excludeStaticAnswers } from '../../labelling/reducers/utils/utils';
import { TransactionalIndexedDBWriter } from '../event-journal/transactional-indexed-db-writer';
import type { SyncAnnotationData } from '../event-journal/types';
import type { AnnotationFramesRecord } from '../types/annotation';
import { removeKeyframeAtIndex } from '../utils/frame-span-utils';
import type { FrameAnnotationKey, IAnnotationPersistence } from './persistence/annotation-persistence';
import { NoOpPersistence } from './persistence/annotation-persistence';
import { DirtySetPersistence } from './persistence/dirty-set-persistence';
import { toFramePayload, toMetadataPayload } from './utils/annotation-mappers';
import { DynamicAnswerSegmentBuilder } from './utils/dynamic-answer-utils';
import { extendSpan } from './utils/extend-span';
import { removeFrameIndexFromSpan } from './utils/remove-frame-index-from-span';
import { sortUpsertFrameIndexToSpan, upsertSpan } from './utils/sort-upsert-frame-index-to-span';
import { sortedInsertKeyframe } from './utils/sorted-insert';
import { IDB_KEY_UPPER_BOUND, VIDEO_ID } from './constants';
import type { AnnotationWritePayload, FrameAnnotationInfo, FrameAnnotationWritePayload, FramePayload, MetadataPayload, SpanT, StudioConfigT, StudioStoreOptionsT } from './types';
export class StudioStore extends TransactionalIndexedDBWriter {
  private readonly options: StudioStoreOptionsT;
  private readonly persistence: IAnnotationPersistence;
  private frameAnnotationCache: AnnotationFramesRecord = new Map();
  private annotationMetadata: Map<string, MetadataPayload> = new Map();
  private memoryRange: SpanT | null = null;
  // Explicit set of chunk indices actually loaded into frameAnnotationCache. memoryRange is
  // just a min/max union of loaded chunk bounds and can span a gap (e.g. prefetch loading a
  // forward chunk without the ones in between) — isChunkInMemory must not infer "loaded" from
  // the range alone.
  private loadedChunks: Set<number> = new Set();
  // Total frame count — needed by evictOutOfWindow to clamp memoryRange.end.
  // Set at the start of init() from the frameCount argument.
  private frameCount = 0;
  // Most recent currentFrameIndex from getAnnotationFrames — used by prefetchWithEvict
  // to evict relative to the latest position rather than a stale closure value.
  // Chunk is derived from this when needed (Math.floor(lastCurrentFrame / chunkSize)).
  private lastCurrentFrame = 0;
  // In-memory record of which frame ranges have been bulk-fetched from the backend.
  // Kept in memory because fetched-ranges writes go through the dirty-set;
  // reading IDB directly would see a stale record if the dirty-set hasn't flushed yet.
  // Seeded in initWindowed() and extended in loadChunk() whenever a new chunk is fetched.
  private knownFetchedRanges: SpanT[] = [];
  // Chunk loads in flight, keyed by chunkIndex — dedupes concurrent loadChunk calls
  // for the same chunk (e.g. playback ticks firing before the previous fetch resolves).
  private inFlightChunkLoads: Map<number, Promise<void>> = new Map();
  constructor(options: StudioStoreOptionsT) {
    super();
    this.options = options;
    this.persistence = options.config.mode === 'windowed' ? new DirtySetPersistence(options.config.taskKey, () => this.petitionForWrite()) : new NoOpPersistence();
  }

  // --- TransactionalIndexedDBWriter delegates ---

  getStores(): readonly string[] {
    return this.persistence.getStores();
  }
  writeToIdb(tx: IDBTransaction, rollbackChannel: (cb: () => void) => void): void {
    this.persistence.writeToIdb(tx, rollbackChannel);
  }

  // --- Lifecycle ---

  async init(frameCount: number): Promise<void> {
    this.frameCount = frameCount;
    if (this.options.config.mode === 'windowed') {
      return this.initWindowed(frameCount, this.options.config);
    }
    return this.initMemory(frameCount, this.options.config);
  }
  async flush(): Promise<void> {
    if (this.options.config.mode !== 'windowed') {
      return;
    }
    // Initiate an IDB write for any pending dirty entries.
    // Fire-and-forget: await doesn't help here since React cleanup and
    // beforeunload don't wait for Promises — we just need to trigger the write.
    this.petitionForWrite();
  }

  // Removes all IDB data for a task after submit — annotation frames, metadata,
  // and fetched-range tracking. Called post-submit to free storage.
  static async purgeTaskData(taskKey: string, videoId: string): Promise<void> {
    await Promise.all([deleteDataByRange(Stores.AnnotationFrames, [taskKey, videoId, 0, ''], [taskKey, videoId, Infinity, IDB_KEY_UPPER_BOUND]), deleteDataByRange(Stores.AnnotationMetadata, [taskKey, videoId, ''], [taskKey, videoId, IDB_KEY_UPPER_BOUND]), deleteData(Stores.FetchedRanges, [taskKey, videoId])]);
  }

  // --- Frame reads ---

  async getAnnotationFrames(currentFrameIndex: number): Promise<AnnotationFramesRecord> {
    this.lastCurrentFrame = currentFrameIndex;
    if (this.options.config.mode !== 'windowed') {
      return this.frameAnnotationCache;
    }
    const {
      chunkSize,
      memoryChunkRadius
    } = this.options.config;
    const targetChunk = Math.floor(currentFrameIndex / chunkSize);
    if (!this.isChunkInMemory(targetChunk)) {
      await this.loadChunk(targetChunk);
      this.evictOutOfWindow(targetChunk, memoryChunkRadius);
    }
    this.schedulePrefetch(targetChunk);
    return this.frameAnnotationCache;
  }
  async getAnnotationFrameAtIndex(frameIndex: number, annotationId: string): Promise<FramePayload> {
    // Tier 1: memory
    const memHit = this.frameAnnotationCache.get(frameIndex)?.get(annotationId);
    if (memHit) {
      return memHit;
    }

    // Tier 2: IDB — only reachable in windowed mode (memory mode has all frames resident)
    if (this.options.config.mode === 'windowed') {
      const {
        taskKey
      } = this.options.config;
      const record = await getDataByKey<AnnotationFrameRecord>(Stores.AnnotationFrames, [taskKey, VIDEO_ID, frameIndex, annotationId]);
      if (record) {
        return {
          geometry: record.geometry,
          isKeyFrame: record.isKeyFrame,
          keyFrameType: record.keyFrameType
        };
      }

      // Tier 3: backend — transient point lookup for interpolation boundary lookups.
      // Does NOT write to IDB or update knownFetchedRanges; only fetches one frame's geometry.
      const entries = await this.options.config.fetchFrames(VIDEO_ID, frameIndex, frameIndex);
      const entry = entries.find(e => e.annotationId === annotationId && e.frameIndex === frameIndex);
      if (entry) {
        return {
          geometry: entry.geometry,
          isKeyFrame: entry.isKeyFrame,
          keyFrameType: entry.keyFrameType
        };
      }
    }
    throw new Error(`Frame ${frameIndex} annotation ${annotationId} not found`);
  }

  // Returns frame payloads for a specific annotation across the given spans.
  // Fetches missing chunks in parallel using range queries — contiguous missing chunks
  // are merged into a single backend or IDB call per run.
  // Tier 1: memory cache → Tier 2: IDB range query → Tier 3: backend fetch + persist.
  // memoryRange is NOT updated — it is tied to navigation via getAnnotationFrames.
  // Memory mode reads directly from frameAnnotationCache (all frames always resident).
  async getAnnotationFramePayloadsInSpans(spans: SpanT[], annotationId: string): Promise<Map<number, FramePayload>> {
    if (this.options.config.mode !== 'windowed') {
      const result = new Map<number, FramePayload>();
      for (const span of spans) {
        for (let f = span.start; f <= span.end; f++) {
          const payload = this.frameAnnotationCache.get(f)?.get(annotationId);
          if (payload) {
            result.set(f, payload);
          }
        }
      }
      return result;
    }
    const {
      chunkSize
    } = this.options.config;

    // Collect unique chunk indices across all spans.
    const allChunkIndices = new Set<number>();
    for (const span of spans) {
      const startChunk = Math.floor(span.start / chunkSize);
      const endChunk = Math.floor(span.end / chunkSize);
      for (let c = startChunk; c <= endChunk; c++) {
        allChunkIndices.add(c);
      }
    }
    const sortedChunks = Array.from(allChunkIndices).sort((a, b) => a - b);

    // Partition: chunks already in the frame cache vs. chunks that need loading.
    const memoryChunks = sortedChunks.filter(c => this.isChunkInMemory(c));
    const chunksToLoad = sortedChunks.filter(c => !this.isChunkInMemory(c));
    const result = new Map<number, FramePayload>();

    // Tier 1: read in-memory chunks directly from frameAnnotationCache.
    for (const c of memoryChunks) {
      const frameStart = c * chunkSize;
      const frameEnd = Math.min(frameStart + chunkSize - 1, this.frameCount - 1);
      for (let f = frameStart; f <= frameEnd; f++) {
        const payload = this.frameAnnotationCache.get(f)?.get(annotationId);
        if (payload) {
          result.set(f, payload);
        }
      }
    }
    if (chunksToLoad.length === 0) {
      return result;
    }

    // Group non-memory chunk indices into contiguous runs using extendSpan.
    // Adjacent chunks extend the current run; a gap starts a new one.
    // e.g. [0,1,2,5,6] → [{start:0,end:2},{start:5,end:6}]
    const chunkRuns = chunksToLoad.reduce(extendSpan, ([] as SpanT[]));

    // Fetch all runs in parallel — each run is one IDB range query or backend call.
    const perRunResults = await Promise.all(chunkRuns.map(({
      start: startChunk,
      end: endChunk
    }) => this.loadChunkRangeAndGetAnnotationPayloads(startChunk, endChunk, annotationId)));
    for (const partial of perRunResults) {
      for (const [frameIndex, payload] of partial) {
        result.set(frameIndex, payload);
      }
    }
    return result;
  }
  getAnnotationMetadata(): Map<string, MetadataPayload> {
    return this.annotationMetadata;
  }

  // --- Frame writes (sole entry points for annotation frame edits) ---

  writeAnnotation({
    frameIndex,
    annotationId,
    payload
  }: FrameAnnotationWritePayload): void {
    this.bulkWriteAnnotations([{
      frameIndex,
      annotationId,
      payload
    }]);
  }
  bulkWriteAnnotations(writes: FrameAnnotationWritePayload[]): void {
    if (writes.length === 0) {
      return;
    }
    let newCache = new Map(this.frameAnnotationCache);
    for (const {
      frameIndex,
      annotationId,
      payload
    } of writes) {
      if (this.isInMemoryRange(frameIndex)) {
        const currentFrame = newCache.get(frameIndex) ?? new Map<string, FramePayload>();
        newCache.set(frameIndex, new Map(currentFrame).set(annotationId, payload));
      }
      this.persistence.onWrite(frameIndex, annotationId, payload);
      this.updateMetadataForAnnotationFrameWrite(frameIndex, annotationId, payload);
    }
    this.frameAnnotationCache = newCache;
  }
  deleteAnnotation({
    frameIndex,
    annotationId
  }: FrameAnnotationInfo): void {
    this.bulkDeleteAnnotations([{
      frameIndex,
      annotationId
    }]);
  }
  bulkDeleteAnnotations(deletes: FrameAnnotationInfo[]): void {
    if (deletes.length === 0) {
      return;
    }
    let modified = false;
    let newCache = new Map(this.frameAnnotationCache);
    for (const {
      frameIndex,
      annotationId
    } of deletes) {
      if (this.isInMemoryRange(frameIndex)) {
        const currentFrame = newCache.get(frameIndex);
        if (currentFrame?.has(annotationId)) {
          const newFrame = new Map(currentFrame);
          newFrame.delete(annotationId);
          newCache.set(frameIndex, newFrame);
          modified = true;
        }
      }
      this.persistence.onDelete(frameIndex, annotationId);
      this.updateMetadataForAnnotationFrameDelete(frameIndex, annotationId);
    }
    if (modified) {
      this.frameAnnotationCache = newCache;
    }
  }
  promoteToKeyFrame(frameIndex: number, annotationId: string): SyncAnnotationData | null {
    const existingPayload = this.frameAnnotationCache.get(frameIndex)?.get(annotationId);
    if (!existingPayload) {
      return null;
    }
    const metadata = this.annotationMetadata.get(annotationId);
    if (!metadata) {
      return null;
    }
    this.bulkWriteAnnotations([{
      frameIndex,
      annotationId,
      payload: {
        ...existingPayload,
        isKeyFrame: true,
        keyFrameType: 'MANUAL',
        labelId: metadata.labelId,
        annotationType: metadata.annotationType,
        originalId: metadata.originalId,
        annotationSource: metadata.annotationSource
      }
    }]);
    return {
      annotationType: metadata.annotationType,
      data: existingPayload.geometry,
      keyFrame: {
        isKeyFrame: true,
        type: 'MANUAL'
      }
    };
  }

  // --- Metadata writes (sole entry points for annotation metadata edits) ---

  deleteMetadata(annotationId: string): void {
    const existing = this.annotationMetadata.get(annotationId);
    if (!existing) {
      return;
    }
    const newCache = new Map(this.annotationMetadata);
    newCache.delete(annotationId);
    this.annotationMetadata = newCache;
    this.persistence.onMetadataDelete(annotationId);
  }
  insertMetadata(annotationId: string, metadata: MetadataPayload): void {
    const existing = this.annotationMetadata.get(annotationId);
    if (existing) {
      return this.updateMetadata(annotationId, metadata);
    }
    const newCache = new Map(this.annotationMetadata);
    newCache.set(annotationId, metadata);
    this.annotationMetadata = newCache;
    this.persistence.onMetadataWrite(annotationId, metadata);
  }
  updateMetadata(annotationId: string, partial: Partial<Omit<MetadataPayload, 'annotationId'>>): void {
    const existing = this.annotationMetadata.get(annotationId);
    if (!existing) {
      return;
    }
    const newCache = new Map(this.annotationMetadata);
    const newMetadata = {
      ...existing,
      ...partial,
      annotationId
    };
    newCache.set(annotationId, newMetadata);
    this.annotationMetadata = newCache;
    this.persistence.onMetadataWrite(annotationId, newMetadata);
  }

  // --- Private: init paths ---

  private async initWindowed(frameCount: number, config: Extract<StudioConfigT, {
    mode: 'windowed';
  }>): Promise<void> {
    const {
      taskKey,
      fetchAnnotationMetadata,
      fetchFrames,
      chunkSize,
      memoryChunkRadius
    } = config;
    const initialWindowEnd = Math.min(chunkSize * (memoryChunkRadius + 1) - 1, frameCount - 1);
    const fetchedRanges = await getDataByKey<FetchedRangesRecord>(Stores.FetchedRanges, [taskKey, VIDEO_ID]);
    const isFirstSession = !fetchedRanges || fetchedRanges.ranges.length === 0;
    if (isFirstSession) {
      const [metadataList, frameEntries] = await Promise.all([fetchAnnotationMetadata(frameCount), fetchFrames(VIDEO_ID, 0, initialWindowEnd)]);

      // Populate in-memory metadata and dirty for persistence
      for (const metadata of metadataList) {
        this.annotationMetadata.set(metadata.annotationId, metadata);
        this.persistence.onMetadataWrite(metadata.annotationId, metadata);
      }

      // Build cache directly from fetched entries — stamp empty maps for all window frames
      const newCache: AnnotationFramesRecord = new Map();
      for (let i = 0; i <= initialWindowEnd; i++) {
        newCache.set(i, new Map<string, FramePayload>());
      }
      for (const entry of frameEntries) {
        const frameMap = newCache.get(entry.frameIndex) ?? new Map<string, FramePayload>();
        frameMap.set(entry.annotationId, {
          geometry: entry.geometry,
          isKeyFrame: entry.isKeyFrame,
          keyFrameType: entry.keyFrameType
        });
        newCache.set(entry.frameIndex, frameMap);
      }
      this.frameAnnotationCache = newCache;

      // Dirty all frame entries for async persistence via debounced flush
      for (const entry of frameEntries) {
        const metadata = this.annotationMetadata.get(entry.annotationId);
        this.persistence.onWrite(entry.frameIndex, entry.annotationId, {
          geometry: entry.geometry,
          isKeyFrame: entry.isKeyFrame,
          keyFrameType: entry.keyFrameType,
          labelId: metadata?.labelId ?? '',
          annotationType: metadata?.annotationType ?? 'RECTANGLE'
        });
      }

      // Mark fetched range — committed atomically with frames and metadata
      this.persistence.onFetchedRangesWrite({
        taskKey,
        videoId: VIDEO_ID,
        ranges: [{
          start: 0,
          end: initialWindowEnd
        }]
      });
    } else {
      await this.loadMetadataFromIdb(taskKey);
      await this.loadInitialWindowFromIdb(taskKey, frameCount, chunkSize, memoryChunkRadius);
    }
    this.memoryRange = frameCount > 0 ? {
      start: 0,
      end: initialWindowEnd
    } : null;
    this.loadedChunks = new Set();
    for (let c = 0; c * chunkSize <= initialWindowEnd; c++) {
      this.loadedChunks.add(c);
    }
    // Restore the full set of previously-fetched ranges (not just the initial window) so
    // chunks fetched in a prior session are read from IDB instead of re-hitting the network.
    this.knownFetchedRanges = isFirstSession ? frameCount > 0 ? [{
      start: 0,
      end: initialWindowEnd
    }] : [] : fetchedRanges.ranges;
  }
  private async initMemory(frameCount: number, config: Extract<StudioConfigT, {
    mode: 'memory';
  }>): Promise<void> {
    const {
      frames
    } = config.initialState;
    const frameAnnotationCache: AnnotationFramesRecord = new Map();
    const annotationMetadata = new Map<string, MetadataPayload>();
    const dynamicAnswersBuilder = new DynamicAnswerSegmentBuilder();
    for (let frameIndex = 0; frameIndex < frameCount; frameIndex++) {
      const frameMap = new Map<string, FramePayload>();
      const annotationStore = frames[frameIndex]?.annotationStore;
      if (annotationStore) {
        for (const [annotationId, annotation] of Object.entries(annotationStore)) {
          if (!annotation) {
            continue;
          }
          const framePayload = toFramePayload(annotation);
          frameMap.set(annotationId, framePayload);
          const existing = annotationMetadata.get(annotationId);
          if (!existing) {
            annotationMetadata.set(annotationId, toMetadataPayload(annotationId, annotation, {
              start: frameIndex,
              end: frameIndex
            }));
          } else {
            existing.spans = extendSpan(existing.spans, frameIndex);
            if (framePayload.isKeyFrame) {
              existing.keyframes = sortedInsertKeyframe(existing.keyframes, {
                frameIndex,
                type: framePayload.keyFrameType ?? 'MANUAL'
              });
            }
          }
          dynamicAnswersBuilder.record(annotationId, frameIndex, excludeStaticAnswers(annotation.metadata?.answers));
        }
      }

      // Always insert — empty frames are part of the cache, matching annotationFramesAtom shape.
      frameAnnotationCache.set(frameIndex, frameMap);
    }
    for (const [annotationId, dynamicAnswers] of dynamicAnswersBuilder.build()) {
      const metadata = annotationMetadata.get(annotationId);
      if (metadata) {
        metadata.dynamicAnswers = dynamicAnswers;
      }
    }
    this.frameAnnotationCache = frameAnnotationCache;
    this.annotationMetadata = annotationMetadata;
    this.memoryRange = frameCount > 0 ? {
      start: 0,
      end: frameCount - 1
    } : null;
  }

  // --- Private: IDB helpers ---

  private async loadMetadataFromIdb(taskKey: string): Promise<void> {
    const records = await getDataByRange<AnnotationMetadataRecord>(Stores.AnnotationMetadata, [taskKey, VIDEO_ID, ''], [taskKey, VIDEO_ID, IDB_KEY_UPPER_BOUND]);
    const metadataMap = new Map<string, MetadataPayload>();
    for (const {
      taskKey: _tk,
      videoId: _vi,
      ...metadata
    } of records) {
      metadataMap.set(metadata.annotationId, metadata);
    }
    this.annotationMetadata = metadataMap;
  }
  private async loadInitialWindowFromIdb(taskKey: string, frameCount: number, chunkSize: number, memoryChunkRadius: number): Promise<void> {
    const windowEnd = Math.min(chunkSize * (memoryChunkRadius + 1) - 1, frameCount - 1);
    const records = await getDataByRange<AnnotationFrameRecord>(Stores.AnnotationFrames, [taskKey, VIDEO_ID, 0],
    // IDB_KEY_UPPER_BOUND ensures records at
    // frameIndex === windowEnd are included (a 3-element upper bound would exclude them).
    [taskKey, VIDEO_ID, windowEnd, IDB_KEY_UPPER_BOUND]);
    this.frameAnnotationCache = this.buildCacheFromIdbRecords(records, 0, windowEnd);
  }

  // Builds an AnnotationFramesRecord from IDB records, stamping empty Maps for all
  // frame indices in [windowStart, windowEnd]. Used by loadInitialWindowFromIdb.
  private buildCacheFromIdbRecords(records: AnnotationFrameRecord[], windowStart: number, windowEnd: number): AnnotationFramesRecord {
    const cache: AnnotationFramesRecord = new Map();
    for (let i = windowStart; i <= windowEnd; i++) {
      cache.set(i, new Map<string, FramePayload>());
    }
    for (const record of records) {
      const frameMap = cache.get(record.frameIndex) ?? new Map<string, FramePayload>();
      frameMap.set(record.annotationId, {
        geometry: record.geometry,
        isKeyFrame: record.isKeyFrame,
        keyFrameType: record.keyFrameType
      });
      cache.set(record.frameIndex, frameMap);
    }
    return cache;
  }

  // --- Private: metadata update helpers ---

  private updateMetadataForAnnotationFrameWrite(frameIndex: number, annotationId: string, payload: AnnotationWritePayload): void {
    const existing = this.annotationMetadata.get(annotationId);
    if (!existing) {
      this.insertMetadata(annotationId, {
        annotationId,
        labelId: payload.labelId,
        annotationType: payload.annotationType,
        originalId: payload.originalId,
        annotationSource: payload.annotationSource,
        spans: [{
          start: frameIndex,
          end: frameIndex
        }],
        keyframes: payload.isKeyFrame ? [{
          frameIndex,
          type: payload.keyFrameType ?? 'MANUAL'
        }] : []
      });
    } else {
      this.updateMetadata(annotationId, {
        spans: sortUpsertFrameIndexToSpan(existing.spans, frameIndex),
        keyframes: payload.isKeyFrame ? sortedInsertKeyframe(existing.keyframes, {
          frameIndex,
          type: payload.keyFrameType ?? 'MANUAL'
        }) : removeKeyframeAtIndex(frameIndex, existing.keyframes)
      });
    }
  }
  private updateMetadataForAnnotationFrameDelete(frameIndex: number, annotationId: string): void {
    const existing = this.annotationMetadata.get(annotationId);
    if (!existing) {
      return;
    }
    const newSpans = removeFrameIndexFromSpan(existing.spans, frameIndex);
    if (newSpans.length === 0) {
      this.deleteMetadata(annotationId);
    } else {
      this.updateMetadata(annotationId, {
        spans: newSpans,
        keyframes: removeKeyframeAtIndex(frameIndex, existing.keyframes)
      });
    }
  }

  // --- Private: utility ---

  private isInMemoryRange(frameIndex: number): boolean {
    return this.memoryRange !== null && frameIndex >= this.memoryRange.start && frameIndex <= this.memoryRange.end;
  }

  // --- Private: windowed chunk management ---

  // Fetches a contiguous chunk range as a single IDB range query or transient backend call,
  // and returns payloads for the requested annotationId only.
  // This is a read-only operation for building undo data — it does NOT persist to IDB,
  // update knownFetchedRanges, or dirty the persistence layer. Callers that also need
  // the frames cached should use loadChunk (via getAnnotationFrames) instead.
  // memoryRange and frameAnnotationCache are NOT updated — caller receives data directly.
  private async loadChunkRangeAndGetAnnotationPayloads(startChunk: number, endChunk: number, annotationId: string): Promise<Map<number, FramePayload>> {
    const config = (this.options.config as Extract<StudioConfigT, {
      mode: 'windowed';
    }>);
    const {
      taskKey,
      chunkSize,
      fetchFrames
    } = config;
    const frameStart = startChunk * chunkSize;
    const frameEnd = Math.min((endChunk + 1) * chunkSize - 1, this.frameCount - 1);
    const result = new Map<number, FramePayload>();
    if (this.isRangeFetched(frameStart, frameEnd)) {
      // Range already in IDB — single range query, filter to annotationId in memory.
      const records = await getDataByRange<AnnotationFrameRecord>(Stores.AnnotationFrames, [taskKey, VIDEO_ID, frameStart, ''], [taskKey, VIDEO_ID, frameEnd, IDB_KEY_UPPER_BOUND]);
      for (const record of records) {
        if (record.annotationId === annotationId) {
          result.set(record.frameIndex, {
            geometry: record.geometry,
            isKeyFrame: record.isKeyFrame,
            keyFrameType: record.keyFrameType
          });
        }
      }
    } else {
      // Not yet fetched — transient backend call for the full range.
      // Does NOT persist to IDB or update knownFetchedRanges: this is read-only undo data,
      // and the caller (delete command) is about to delete these frames immediately.
      // Persisting then deleting the same frames would dirty the persistence layer twice.
      const entries = await fetchFrames(VIDEO_ID, frameStart, frameEnd);
      for (const entry of entries) {
        if (entry.annotationId === annotationId) {
          result.set(entry.frameIndex, {
            geometry: entry.geometry,
            isKeyFrame: entry.isKeyFrame,
            keyFrameType: entry.keyFrameType
          });
        }
      }
    }
    return result;
  }
  private isChunkInMemory(chunkIndex: number): boolean {
    if (this.options.config.mode !== 'windowed') {
      return true;
    }
    return this.loadedChunks.has(chunkIndex);
  }

  // Checks whether [start, end] is fully covered by knownFetchedRanges.
  // Uses the in-memory record because fetched-ranges writes go through the dirty-set
  // and may not be flushed to IDB yet — reading IDB directly would see a stale record.
  private isRangeFetched(start: number, end: number): boolean {
    return this.knownFetchedRanges.some(range => range.start <= start && range.end >= end);
  }
  private loadChunk(chunkIndex: number): Promise<void> {
    const existing = this.inFlightChunkLoads.get(chunkIndex);
    if (existing) {
      return existing;
    }
    const promise = this.loadChunkImpl(chunkIndex).finally(() => {
      this.inFlightChunkLoads.delete(chunkIndex);
    });
    this.inFlightChunkLoads.set(chunkIndex, promise);
    return promise;
  }
  private async loadChunkImpl(chunkIndex: number): Promise<void> {
    const config = (this.options.config as Extract<StudioConfigT, {
      mode: 'windowed';
    }>);
    const {
      taskKey,
      chunkSize,
      fetchFrames
    } = config;
    const chunkStart = chunkIndex * chunkSize;
    const chunkEnd = Math.min(chunkStart + chunkSize - 1, this.frameCount - 1);
    if (this.isRangeFetched(chunkStart, chunkEnd)) {
      // Chunk already persisted in IDB — load from there, but also merge in
      // not-yet-flushed dirty-set writes (e.g. an annotation just drawn at the
      // chunk boundary before the debounced IDB flush lands).
      const records = await getDataByRange<AnnotationFrameRecord>(Stores.AnnotationFrames, [taskKey, VIDEO_ID, chunkStart, ''], [taskKey, VIDEO_ID, chunkEnd, IDB_KEY_UPPER_BOUND]);
      const pendingLocalRecords = this.persistence.getDirtyRecordsInRange(chunkStart, chunkEnd);
      // Pending records last so they win over IDB for the same frame+annotation key;
      // deletedKeys always win over both, regardless of order.
      this.addChunkToCache([...records, ...pendingLocalRecords.upserts], chunkStart, chunkEnd, pendingLocalRecords.deletedKeys);
    } else {
      // Chunk not yet fetched from the backend. But client-side writes that land outside
      // the loaded window (e.g. interpolating a keyframe across many chunks) may have
      // already dirtied or flushed frames in this chunk — snapshot those *before* the
      // backend-entries loop below writes into the same dirty set, so a same-key collision
      // can't clobber the local edit before we read it back.
      const [entries, alreadyFlushedLocalRecords] = await Promise.all([fetchFrames(VIDEO_ID, chunkStart, chunkEnd), getDataByRange<AnnotationFrameRecord>(Stores.AnnotationFrames, [taskKey, VIDEO_ID, chunkStart, ''], [taskKey, VIDEO_ID, chunkEnd, IDB_KEY_UPPER_BOUND])]);
      const {
        upserts: pendingLocalRecords,
        deletedKeys
      } = this.persistence.getDirtyRecordsInRange(chunkStart, chunkEnd);
      const deletedKeySet = new Set(deletedKeys.map(({
        frameIndex,
        annotationId
      }) => `${frameIndex}:${annotationId}`));
      // Entries the user already deleted locally (pending, not yet flushed) must not be
      // re-dirtied from the backend fetch — that would overwrite the 'DELETE' sentinel in
      // the dirty set and permanently resurrect the annotation once the flush lands in IDB.
      const liveEntries = entries.filter(entry => !deletedKeySet.has(`${entry.frameIndex}:${entry.annotationId}`));
      for (const entry of liveEntries) {
        const metadata = this.annotationMetadata.get(entry.annotationId);
        this.persistence.onWrite(entry.frameIndex, entry.annotationId, {
          geometry: entry.geometry,
          isKeyFrame: entry.isKeyFrame,
          keyFrameType: entry.keyFrameType,
          labelId: metadata?.labelId ?? '',
          annotationType: metadata?.annotationType ?? 'RECTANGLE'
        });
      }

      // Merge new chunk into knownFetchedRanges and dirty the updated record for IDB flush.
      this.knownFetchedRanges = upsertSpan(this.knownFetchedRanges, {
        start: chunkStart,
        end: chunkEnd
      });
      this.persistence.onFetchedRangesWrite({
        taskKey,
        videoId: VIDEO_ID,
        ranges: this.knownFetchedRanges
      });

      // petitionNow ensures chunk frames reach IDB before potential eviction.
      // The debounce (300ms) could fire after the user has already scrolled far enough
      // to evict this chunk; a subsequent reload would then find stale IDB data.
      // petitionNow() is a no-op when the dirty-set is empty (guarded inside DirtySetPersistence).
      this.persistence.petitionNow();
      const backendRecords: AnnotationFrameRecord[] = liveEntries.map(entry => ({
        taskKey,
        videoId: VIDEO_ID,
        frameIndex: entry.frameIndex,
        annotationId: entry.annotationId,
        geometry: entry.geometry,
        isKeyFrame: entry.isKeyFrame,
        keyFrameType: entry.keyFrameType
      }));
      // Local records last so they win over the backend's for the same frame+annotation
      // (addChunkToCache lets later entries overwrite earlier ones for the same key);
      // deletedKeys always win over all of them, regardless of order.
      this.addChunkToCache([...backendRecords, ...alreadyFlushedLocalRecords, ...pendingLocalRecords], chunkStart, chunkEnd, deletedKeys);
    }

    // Extend memoryRange to include the newly loaded chunk.
    this.memoryRange = this.memoryRange ? {
      start: Math.min(this.memoryRange.start, chunkStart),
      end: Math.max(this.memoryRange.end, chunkEnd)
    } : {
      start: chunkStart,
      end: chunkEnd
    };
    this.loadedChunks.add(chunkIndex);
  }

  // Merges new chunk frames into the existing frameAnnotationCache.
  // Stamps empty Maps for annotation-free frames so repeated chunk-miss checks are prevented.
  // deletedKeys are applied last so a pending delete always wins over a stale IDB/backend
  // record for the same frame+annotation, regardless of record order.
  private addChunkToCache(records: AnnotationFrameRecord[], chunkStart: number, chunkEnd: number, deletedKeys: FrameAnnotationKey[] = []): void {
    const newCache = new Map(this.frameAnnotationCache);
    for (let i = chunkStart; i <= chunkEnd; i++) {
      if (!newCache.has(i)) {
        newCache.set(i, new Map<string, FramePayload>());
      }
    }
    for (const record of records) {
      const existing = newCache.get(record.frameIndex) ?? new Map<string, FramePayload>();
      const updated = new Map(existing);
      updated.set(record.annotationId, {
        geometry: record.geometry,
        isKeyFrame: record.isKeyFrame,
        keyFrameType: record.keyFrameType
      });
      newCache.set(record.frameIndex, updated);
    }
    for (const {
      frameIndex,
      annotationId
    } of deletedKeys) {
      const existing = newCache.get(frameIndex);
      if (existing?.has(annotationId)) {
        const updated = new Map(existing);
        updated.delete(annotationId);
        newCache.set(frameIndex, updated);
      }
    }
    this.frameAnnotationCache = newCache;
  }

  // Eviction happens AFTER loadChunk — never before.
  private evictOutOfWindow(currentChunk: number, radius: number): void {
    const config = (this.options.config as Extract<StudioConfigT, {
      mode: 'windowed';
    }>);
    const {
      chunkSize
    } = config;
    const keepStart = (currentChunk - radius) * chunkSize;
    const keepEnd = Math.min((currentChunk + radius + 1) * chunkSize - 1, this.frameCount - 1);

    // Flush dirty writes before evicting frames from memory.
    // Example: user edits frames in chunk 2 then scrolls to chunk 8 (triggering eviction of chunk 2).
    // Without petitionNow(), those edits may still be in the dirty-set when chunk 2 is evicted.
    // If the user scrolls back to chunk 2, loadChunk would read stale IDB data missing those edits.
    // petitionNow() is a no-op when the dirty-set is empty (guarded inside DirtySetPersistence).
    this.persistence.petitionNow();
    const newCache = new Map<number, Map<string, FramePayload>>();
    for (const [frameIndex, frameMap] of this.frameAnnotationCache) {
      if (frameIndex >= keepStart && frameIndex <= keepEnd) {
        newCache.set(frameIndex, frameMap);
      }
    }
    this.frameAnnotationCache = newCache;

    // Shrink memoryRange to reflect the eviction.
    this.memoryRange = {
      start: keepStart,
      end: keepEnd
    };
    for (const chunkIndex of this.loadedChunks) {
      if (chunkIndex * chunkSize < keepStart || chunkIndex * chunkSize > keepEnd) {
        this.loadedChunks.delete(chunkIndex);
      }
    }
  }
  private schedulePrefetch(currentChunk: number): void {
    const config = (this.options.config as Extract<StudioConfigT, {
      mode: 'windowed';
    }>);
    const {
      chunkSize,
      memoryChunkRadius
    } = config;

    // Prefetch one chunk beyond the forward edge of the memory window.
    const forwardChunk = currentChunk + memoryChunkRadius + 1;
    if (!this.isChunkInMemory(forwardChunk) && forwardChunk * chunkSize < this.frameCount) {
      requestIdleCallback(() => {
        this.prefetchWithEvict(forwardChunk).catch(() => {});
      });
    }

    // Prefetch one chunk beyond the backward edge of the memory window.
    const backwardChunk = currentChunk - memoryChunkRadius - 1;
    if (backwardChunk >= 0 && !this.isChunkInMemory(backwardChunk)) {
      requestIdleCallback(() => {
        this.prefetchWithEvict(backwardChunk).catch(() => {});
      });
    }
  }

  // Loads a chunk in the background then evicts based on the most recent frame position.
  // lastCurrentFrame is updated on every getAnnotationFrames call, so by the time
  // requestIdleCallback fires the user may have scrolled — evicting relative to the
  // stale closure chunk would remove the wrong frames.
  private async prefetchWithEvict(chunkToLoad: number): Promise<void> {
    const config = (this.options.config as Extract<StudioConfigT, {
      mode: 'windowed';
    }>);
    const {
      chunkSize,
      memoryChunkRadius
    } = config;
    await this.loadChunk(chunkToLoad);
    const centerChunk = Math.floor(this.lastCurrentFrame / chunkSize);
    this.evictOutOfWindow(centerChunk, memoryChunkRadius);
  }
}